import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { MODULES } from "../../config/modules";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import {
  Home,
  FileText,
  FolderKanban,
  Settings,
  LogOut,
  Menu,
  ChevronLeft,
} from "lucide-react";

const iconMap = {
  contracts: FileText,
  proposals: FolderKanban,
  admin: Settings,
};

export default function AppLayout({ children }) {
  const { profile, logout, checkRole } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const accessibleModules = MODULES.filter(
    (m) => !m.requiredRole || checkRole(m.requiredRole)
  );

  const getRoleBadgeVariant = (role) => {
    if (role === "super-admin") return "purple";
    if (role === "admin") return "default";
    return "secondary";
  };

  const NavItem = ({ to, icon: Icon, label, isActive }) => (
    <Link
      to={to}
      onClick={() => setMobileOpen(false)}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-accent",
        isActive ? "bg-accent text-accent-foreground font-medium" : "text-muted-foreground",
        collapsed && "justify-center px-2"
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span>{label}</span>}
    </Link>
  );

  const SidebarContent = () => (
    <>
      {/* Header */}
      <div className={cn("flex h-14 items-center border-b px-4", collapsed && "justify-center px-2")}>
        {!collapsed && (
          <Link to="/" className="flex items-center gap-2 font-semibold">
            <div className="h-6 w-6 rounded bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold">
              C
            </div>
            <span>Caladyn</span>
          </Link>
        )}
        {collapsed && (
          <div className="h-6 w-6 rounded bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold">
            C
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-auto py-4">
        <nav className="grid gap-1 px-2">
          <NavItem
            to="/"
            icon={Home}
            label="Home"
            isActive={location.pathname === "/"}
          />
          <Separator className="my-2" />
          {accessibleModules.map((m) => {
            const Icon = iconMap[m.id] || FileText;
            return (
              <NavItem
                key={m.id}
                to={m.path}
                icon={Icon}
                label={m.label}
                isActive={location.pathname === m.path || location.pathname.startsWith(m.path + "/")}
              />
            );
          })}
        </nav>
      </div>

      {/* Footer - User info */}
      <div className="border-t p-4">
        <div className={cn("flex items-center gap-3", collapsed && "flex-col gap-2")}>
          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium shrink-0">
            {profile?.email?.charAt(0).toUpperCase()}
          </div>
          {!collapsed && (
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-medium truncate">{profile?.email}</p>
              <Badge variant={getRoleBadgeVariant(profile?.role)} className="mt-1">
                {profile?.role}
              </Badge>
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size={collapsed ? "icon" : "default"}
          className={cn("w-full mt-3", collapsed && "w-8 h-8")}
          onClick={logout}
        >
          <LogOut className="h-4 w-4" />
          {!collapsed && <span className="ml-2">Logout</span>}
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "hidden md:flex flex-col border-r bg-card transition-all duration-300",
          collapsed ? "w-16" : "w-64"
        )}
      >
        <SidebarContent />
        <Button
          variant="ghost"
          size="icon"
          className="absolute bottom-20 -right-3 h-6 w-6 rounded-full border bg-background shadow-sm hidden md:flex"
          onClick={() => setCollapsed(!collapsed)}
        >
          <ChevronLeft className={cn("h-3 w-3 transition-transform", collapsed && "rotate-180")} />
        </Button>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 flex flex-col border-r bg-card transform transition-transform duration-300 md:hidden",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <SidebarContent />
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header */}
        <header className="flex md:hidden h-14 items-center gap-4 border-b bg-card px-4">
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <span className="font-semibold">Caladyn</span>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
